import React, { useMemo } from 'react';

import { motion } from 'framer-motion';

type StrengthLevel = 0 | 1 | 2 | 3 | 4;

interface LevelConfig {
  label: string;
  barClass: string;
  trackClass: string;
  textClass: string;
  width: string;
}

const strengthConfig: Record<StrengthLevel, LevelConfig> = {
  0: {
    label: '',
    barClass: 'bg-red-500',
    trackClass: 'bg-red-100 dark:bg-red-950/30',
    textClass: 'text-red-600 dark:text-red-400',
    width: '0%',
  },
  1: {
    label: 'Weak',
    barClass: 'bg-red-500',
    trackClass: 'bg-red-100 dark:bg-red-950/30',
    textClass: 'text-red-600 dark:text-red-400',
    width: '25%',
  },
  2: {
    label: 'Fair',
    barClass: 'bg-orange-400',
    trackClass: 'bg-orange-100 dark:bg-orange-950/30',
    textClass: 'text-orange-600 dark:text-orange-400',
    width: '50%',
  },
  3: {
    label: 'Good',
    barClass: 'bg-yellow-400',
    trackClass: 'bg-yellow-100 dark:bg-yellow-950/30',
    textClass: 'text-yellow-600 dark:text-yellow-400',
    width: '75%',
  },
  4: {
    label: 'Strong',
    barClass: 'bg-green-500',
    trackClass: 'bg-green-100 dark:bg-green-950/30',
    textClass: 'text-green-600 dark:text-green-400',
    width: '100%',
  },
};

function calcStrength(password: string): StrengthLevel {
  if (!password) return 0;
  let score = 0;
  if (password.length >= 8) score += 1;
  if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score += 1;
  if (/[0-9]/.test(password)) score += 1;
  if (/[^a-zA-Z0-9]/.test(password)) score += 1;
  return Math.min(score, 4) as StrengthLevel;
}

interface PasswordStrengthMeterProps {
  password: string;
}

const PasswordStrengthMeter: React.FC<PasswordStrengthMeterProps> = React.memo(({ password }) => {
  const level = useMemo(() => calcStrength(password), [password]);
  const config = strengthConfig[level];

  return (
    <div className="space-y-1 px-1">
      <div className={`h-1.5 w-full overflow-hidden rounded-full ${config.trackClass}`}>
        <motion.div
          layout
          transition={{ type: 'spring', stiffness: 300, damping: 25 }}
          className={`h-full rounded-full ${config.barClass}`}
          animate={{ width: password ? config.width : '0%' }}
        />
      </div>
      {password && level > 0 && (
        <motion.p
          key={level}
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          className={`text-[10px] font-bold tracking-wide ${config.textClass}`}>
          {config.label}
        </motion.p>
      )}
    </div>
  );
});

PasswordStrengthMeter.displayName = 'PasswordStrengthMeter';

export default PasswordStrengthMeter;
